import { debug } from '../../utils/debug';
import React, { useState } from 'react';
import { m } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiActivity } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { getDeliveryType, DELIVERY_TYPES } from '../../utils/deliveryType';
import { getMerchantId, buildPath } from '../../utils/routing';
import LanguageSwitcher from './LanguageSwitcher';
import { useMotionConfig } from '../../hooks/useReducedMotion';


const Header = ({ currentStep, onStepClick, bookingData, merchantData, enteredViaDeepLink = false }) => {
  const navigate = useNavigate();
  const merchantId = getMerchantId();
  const motion = useMotionConfig();
  const [logoError, setLogoError] = useState(false);
  const logoUrl = merchantData?.config?.logo_url;
  const logoWidth = merchantData?.config?.logo_width || 40; // Default 40px
  const hasValidLogo = logoUrl && !logoError;
  const theme = useTheme();
  const { getMerchantTranslation } = useLanguage();

  // Get merchant translations
  const merchantTranslations = merchantData?.config?.translations || [];

  // Get translated header texts
  const headerText1 = getMerchantTranslation(
    merchantData?.config?.header_text_1,
    merchantTranslations,
    'header_text_1'
  );
  const headerText2 = getMerchantTranslation(
    merchantData?.config?.header_text_2,
    merchantTranslations,
    'header_text_2'
  );
  const headerText3 = getMerchantTranslation(
    merchantData?.config?.header_text_3,
    merchantTranslations,
    'header_text_3'
  );
  const businessName = getMerchantTranslation(
    merchantData?.config?.business_name,
    merchantTranslations,
    'business_name'
  );

  const handleLogoClick = () => {
    if (enteredViaDeepLink) {
      // In deep-link mode, navigate to the first step (timing or delivery-area) instead of locations
      const firstStep = bookingData?.location?._apiData?.deliveries || bookingData?.location?.deliveries
        ? 'delivery-area'
        : 'timing';
      const locationSlug = bookingData?.location?._apiData?.location_slug || bookingData?.location?.slug;
      debug.log('Logo clicked - navigating to first step in deep-link mode:', firstStep);
      navigate(buildPath(`/${locationSlug}/${firstStep}`, merchantId));
    } else {
      // Normal flow: navigate to locations
      debug.log('Logo clicked - navigating to locations');
      navigate(buildPath('/locations', merchantId));
    }
  };
  const getSteps = () => {
    // Detect delivery type
    const deliveryType = bookingData?.location ? getDeliveryType(bookingData.location) : DELIVERY_TYPES.NONE;
    const isDelivery = deliveryType !== DELIVERY_TYPES.NONE;

    if (isDelivery) {
      // Delivery flow steps
      const deliverySteps = [];

      // Only include Location step if NOT entered via deep-link
      if (!enteredViaDeepLink) {
        deliverySteps.push({ id: 1, name: 'Location' });
      }

      const baseId = enteredViaDeepLink ? 0 : 1;

      // DELIVERY_AREAS flow includes the delivery area selection step
      if (deliveryType === DELIVERY_TYPES.DELIVERY_AREAS) {
        deliverySteps.push(
          { id: baseId + 1, name: 'Delivery Area' },
          { id: baseId + 2, name: 'Duration' },
          { id: baseId + 3, name: 'Date' },
          { id: baseId + 4, name: 'Time' },
          { id: baseId + 5, name: 'Bikes' },
          { id: baseId + 6, name: 'Delivery Address' },
          { id: baseId + 7, name: 'Payment' }
        );
      }
      // DELIVERY_DISTANCE flow skips the delivery area selection step
      else if (deliveryType === DELIVERY_TYPES.DELIVERY_DISTANCE) {
        deliverySteps.push(
          { id: baseId + 1, name: 'Duration' },
          { id: baseId + 2, name: 'Date' },
          { id: baseId + 3, name: 'Time' },
          { id: baseId + 4, name: 'Bikes' },
          { id: baseId + 5, name: 'Delivery Address' },
          { id: baseId + 6, name: 'Payment' }
        );
      }

      return deliverySteps;
    }

    // Regular pickup flow
    const baseSteps = [];

    // Only include Location step if NOT entered via deep-link
    if (!enteredViaDeepLink) {
      baseSteps.push({ id: 1, name: 'Location' });
    }

    const baseId = enteredViaDeepLink ? 0 : 1;
    baseSteps.push(
      { id: baseId + 1, name: 'Timing' },
      { id: baseId + 2, name: 'Duration' }
    );

    if (bookingData?.timing !== 'immediate') {
      baseSteps.push(
        { id: baseId + 3, name: 'Date' },
        { id: baseId + 4, name: 'Time' }
      );
    }

    const bikeStepId = bookingData?.timing === 'immediate' ? baseId + 3 : baseId + 5;
    const paymentStepId = bookingData?.timing === 'immediate' ? baseId + 4 : baseId + 6;

    baseSteps.push(
      { id: bikeStepId, name: 'Bikes' },
      { id: paymentStepId, name: 'Payment' }
    );

    return baseSteps;
  };

  const steps = getSteps();

  const getDisplayStep = (internalStep) => {
    // Check if this is a delivery location - only check location flags
    const isDelivery =
      bookingData?.location?._apiData?.deliveries === true ||
      bookingData?.location?.deliveries === true;

    // Delivery flow: step IDs already match display order (1-8)
    if (isDelivery) {
      return internalStep;
    }

    // Regular pickup flow with immediate timing: remap step IDs
    if (bookingData?.timing === 'immediate') {
      const mapping = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 };
      return mapping[internalStep] || internalStep;
    }

    // Regular pickup flow with scheduled timing: no remapping needed
    return internalStep;
  };

  // currentStep is null on post-flow pages (e.g. confirmation) where no step
  // is active. Hide the progress bar, mobile/desktop step counters in that
  // case — keep the rest of the header (logo, language switcher).
  const isPostFlow = currentStep == null;
  const currentDisplayStep = isPostFlow ? null : getDisplayStep(currentStep);
  const totalSteps = steps.length;

  return (
    <m.header
      {...motion.fadeInUp}
      className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40 relative"
    >
      {/* Progress Bar (Mobile only - at top of header). Hidden post-flow. */}
      {!isPostFlow && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 lg:hidden">
          <div
            className="h-full transition-all duration-300 ease-out"
            style={{
              width: `${(currentDisplayStep / totalSteps) * 100}%`,
              backgroundColor: theme.colors.primary.base
            }}
          />
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex lg:grid items-center lg:grid-cols-3 justify-between gap-4">
          {/* Left: Logo Section - Always clickable */}
          <button
            onClick={handleLogoClick}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer justify-self-start"
            type="button"
            aria-label={enteredViaDeepLink ? 'Return to first step' : 'Return to location selection'}
          >
            {hasValidLogo ? (
              <div
                className="flex items-center justify-center"
                style={{ width: `${logoWidth}px`, maxHeight: '80px' }}
              >
                <img
                  src={logoUrl}
                  alt={merchantData.config.display_name || merchantData.config.business_name || 'Logo'}
                  className="object-contain"
                  style={{ width: `${logoWidth}px`, height: 'auto', maxHeight: '80px' }}
                  onError={() => setLogoError(true)}
                />
              </div>
            ) : (
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: theme.colors.primary.gradient }}
              >
                <SafeIcon icon={FiActivity} className="w-5 h-5" style={{ color: theme.colors.primary.contrastIcon }} />
              </div>
            )}
            <div className="text-left">
              {merchantData.config.display_name && (
                <div className="text-xl font-bold text-gray-900">{merchantData.config.display_name}</div>
              )}
              {businessName && (
                <p className="text-sm text-gray-500">{businessName}</p>
              )}
            </div>
          </button>

          {/* Center: Step Counter (Desktop only). Hidden post-flow; render
              an empty placeholder so the right column stays in grid col 3. */}
          {!isPostFlow ? (
            <div className="hidden lg:flex items-center space-x-2 justify-self-center">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: theme.colors.primary.base }}
              >
                <span className="text-sm font-bold" style={{ color: theme.colors.primary.contrastText }}>{currentDisplayStep}</span>
              </div>
              <div className="text-sm text-gray-600">
                of {totalSteps}
              </div>
            </div>
          ) : (
            <div className="hidden lg:block" aria-hidden="true" />
          )}

          {/* Right: Language Switcher and Features */}
          <div className="flex items-center gap-4 lg:gap-8 justify-self-end">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Features (Desktop Only) - Dynamic from merchant config */}
            <div className="hidden lg:flex items-center space-x-6 text-sm text-gray-600">
              {headerText1 && <span>{headerText1}</span>}
              {headerText2 && <span>{headerText2}</span>}
              {headerText3 && <span>{headerText3}</span>}
            </div>
          </div>
        </div>
      </div>
    </m.header>
  );
};

export default Header;